/**
 * Coups de pouce — Attendu 655 « Cercle, disque, rayon, diamètre, corde ».
 *
 * Une entrée par `generator_key` (cf. gen_655.js). Contrat d'une entrée :
 *   pages : 2 pages MAXIMUM, chacune { titre, lignes?, svg? }.
 *           Page 1 « La règle »   : les phrases du mini-cours.
 *           Page 2 « Un exemple » : la figure qui les illustre.
 *
 * Règle absolue : l'aide porte sur d'AUTRES lettres et d'AUTRES valeurs que
 * celles que le générateur peut tirer, sinon elle donne la réponse.
 * Emphase : **texte** (rendu en gras, pas de HTML brut). Pas de tiret cadratin.
 */

// ── Figure Ex. 1 : le lexique illustré ──
// L'exercice pose des affirmations Vrai/Faux sur SA figure, où rien n'est nommé :
// l'élève doit identifier lui-même ce qu'est [CD]. Ici les trois objets sont
// ÉTIQUETÉS, sur un cercle différent et avec d'autres lettres. C'est un lexique,
// pas l'exercice résolu. Sans lui, « une corde joint deux points du cercle »
// resterait purement verbal pour un élève qui n'en a jamais vu.
const SVG_LEXIQUE_CERCLE = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 360 200" role="img"
     aria-label="Un cercle de centre K portant un rayon KP, un diamètre QS et une corde RT">
  <circle cx="110" cy="100" r="70" stroke="#0f172a" fill="none" stroke-width="2"/>

  <line x1="44" y1="76" x2="176" y2="124" stroke="#F97316" stroke-width="2.5"/>
  <line x1="49" y1="135" x2="110" y2="170" stroke="#1D9E75" stroke-width="2.5"/>
  <line x1="110" y1="100" x2="145" y2="39" stroke="#378ADD" stroke-width="2.5"/>

  <circle cx="110" cy="100" r="3" fill="#0f172a"/>
  <circle cx="145" cy="39" r="3" fill="#0f172a"/>
  <circle cx="44" cy="76" r="3" fill="#0f172a"/>
  <circle cx="176" cy="124" r="3" fill="#0f172a"/>
  <circle cx="110" cy="170" r="3" fill="#0f172a"/>
  <circle cx="49" cy="135" r="3" fill="#0f172a"/>

  <g font-size="12" fill="#0f172a">
    <text x="96" y="104" text-anchor="end">K</text>
    <text x="151" y="33">P</text>
    <text x="33" y="76" text-anchor="end">Q</text>
    <text x="184" y="132">S</text>
    <text x="110" y="188" text-anchor="middle">R</text>
    <text x="39" y="145" text-anchor="end">T</text>
  </g>

  <g stroke-width="3">
    <line x1="204" y1="68" x2="226" y2="68" stroke="#378ADD"/>
    <line x1="204" y1="98" x2="226" y2="98" stroke="#F97316"/>
    <line x1="204" y1="128" x2="226" y2="128" stroke="#1D9E75"/>
  </g>
  <g font-size="12.5" fill="#334155">
    <text x="234" y="72">rayon [KP]</text>
    <text x="234" y="102">diamètre [QS]</text>
    <text x="234" y="132">corde [RT]</text>
  </g>
  <text x="204" y="168" font-size="11.5" font-weight="700" fill="#E24B4A">K n'est pas sur le cercle.</text>
</svg>`.trim();

// ── Figure Ex. 2 : les CINQ termes ──
// Ce QCM tire les cinq termes, donc l'exemple doit les couvrir tous. Les coups
// de pouce sont indépendants : un élève qui tombe sur ce format ne verra jamais
// celui de l'exo 1, on ne peut donc pas s'appuyer dessus pour les segments.
// Le cercle porte les trois segments ; le disque rempli tranche la distinction
// contour / intérieur, la seule que les mots rendent mal.
const SVG_CERCLE_DISQUE = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 360 232" role="img"
     aria-label="À gauche un cercle portant un rayon, une corde et un diamètre ; à droite un disque entièrement rempli">
  <circle cx="95" cy="80" r="58" stroke="#1E3A8A" fill="none" stroke-width="2.5"/>
  <line x1="41" y1="60" x2="149" y2="100" stroke="#F97316" stroke-width="2.5"/>
  <line x1="51" y1="117" x2="105" y2="137" stroke="#1D9E75" stroke-width="2.5"/>
  <line x1="95" y1="80" x2="128" y2="33" stroke="#378ADD" stroke-width="2.5"/>
  <g fill="#0f172a">
    <circle cx="95" cy="80" r="2.5"/><circle cx="128" cy="33" r="2.5"/>
    <circle cx="41" cy="60" r="2.5"/><circle cx="149" cy="100" r="2.5"/>
    <circle cx="51" cy="117" r="2.5"/><circle cx="105" cy="137" r="2.5"/>
  </g>
  <text x="95" y="160" text-anchor="middle" font-size="15" font-weight="700" fill="#1E3A8A">cercle</text>
  <text x="95" y="177" text-anchor="middle" font-size="11" fill="#64748b">le contour seul</text>

  <circle cx="268" cy="80" r="58" fill="#93C5FD" stroke="#1E3A8A" stroke-width="2.5"/>
  <circle cx="268" cy="80" r="2.5" fill="#0f172a"/>
  <text x="268" y="160" text-anchor="middle" font-size="15" font-weight="700" fill="#1E3A8A">disque</text>
  <text x="268" y="177" text-anchor="middle" font-size="11" fill="#64748b">le contour et l'intérieur</text>

  <g stroke-width="3">
    <line x1="30" y1="206" x2="52" y2="206" stroke="#378ADD"/>
    <line x1="130" y1="206" x2="152" y2="206" stroke="#1D9E75"/>
    <line x1="226" y1="206" x2="248" y2="206" stroke="#F97316"/>
  </g>
  <g font-size="12.5" fill="#334155">
    <text x="58" y="210">rayon</text>
    <text x="158" y="210">corde</text>
    <text x="254" y="210">diamètre</text>
  </g>
</svg>`.trim();

// ── Figure Ex. 3a : l'échelle de conversion, sur la page « La règle » ──
// Reprise à l'identique de gen_653 : l'élève retrouve un visuel déjà rencontré.
// dm est inclus bien que ce format n'utilise que cm et mm — une table de
// conversion se lit comme une échelle, l'amputer d'un cran la rend moins claire.
const SVG_CONVERSION = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 360 150" role="img"
     aria-label="Échelle de conversion : 1 dm égale 10 cm égale 100 mm">
  <g stroke="#F97316" stroke-width="1.8" fill="none">
    <path d="M102 34 L140 34"/>
    <path d="M212 34 L250 34"/>
  </g>
  <polygon points="146,34 136,30 136,38" fill="#F97316"/>
  <polygon points="256,34 246,30 246,38" fill="#F97316"/>
  <text x="123" y="26" text-anchor="middle" font-size="12" font-weight="700" fill="#F97316">× 10</text>
  <text x="233" y="26" text-anchor="middle" font-size="12" font-weight="700" fill="#F97316">× 10</text>

  <g fill="#EFF6FF" stroke="#1E3A8A" stroke-width="1.5">
    <rect x="38" y="44" width="64" height="34" rx="6"/>
    <rect x="148" y="44" width="64" height="34" rx="6"/>
    <rect x="258" y="44" width="64" height="34" rx="6"/>
  </g>
  <g font-size="17" font-weight="700" fill="#1E3A8A" text-anchor="middle">
    <text x="70" y="67">dm</text>
    <text x="180" y="67">cm</text>
    <text x="290" y="67">mm</text>
  </g>

  <g stroke="#64748b" stroke-width="1.5" fill="none">
    <path d="M140 96 L102 96"/>
    <path d="M250 96 L212 96"/>
  </g>
  <polygon points="96,96 106,92 106,100" fill="#64748b"/>
  <polygon points="206,96 216,92 216,100" fill="#64748b"/>
  <text x="123" y="114" text-anchor="middle" font-size="12" font-weight="700" fill="#64748b">÷ 10</text>
  <text x="233" y="114" text-anchor="middle" font-size="12" font-weight="700" fill="#64748b">÷ 10</text>

  <text x="180" y="140" text-anchor="middle" font-size="14" font-weight="700" fill="#0f172a">1 dm = 10 cm = 100 mm</text>
</svg>`.trim();

// ── Figure Ex. 3b : le diamètre vaut deux rayons ──
// r = 18 cm : le générateur tire r ∈ [2..15] et D ∈ [3..30], donc 18 et 36 sont
// hors de portée. Le diamètre est dessiné comme DEUX rayons bout à bout — c'est
// ce que « deux fois » veut dire, et ça se voit avant de se calculer. Les deux
// mots de vocabulaire sont écrits SUR la figure, à côté de ce qu'ils désignent.
const SVG_RAYON_DIAMETRE = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 360 232" role="img"
     aria-label="Un cercle dont le rayon mesure 18 cm et le diamètre 36 cm, soit 360 mm">
  <circle cx="150" cy="82" r="66" stroke="#1E3A8A" fill="none" stroke-width="2.5"/>
  <line x1="84" y1="82" x2="150" y2="82" stroke="#94a3b8" stroke-width="2.5"/>
  <line x1="150" y1="82" x2="216" y2="82" stroke="#378ADD" stroke-width="3"/>
  <circle cx="150" cy="82" r="3" fill="#0f172a"/>
  <circle cx="84" cy="82" r="3" fill="#0f172a"/>
  <circle cx="216" cy="82" r="3" fill="#0f172a"/>
  <text x="183" y="60" text-anchor="middle" font-size="12.5" font-weight="700" fill="#378ADD">rayon</text>
  <text x="183" y="76" text-anchor="middle" font-size="12" fill="#378ADD">18 cm</text>
  <text x="117" y="76" text-anchor="middle" font-size="12" fill="#64748b">18 cm</text>

  <path d="M84 158 L84 164 L216 164 L216 158" fill="none" stroke="#F97316" stroke-width="1.5"/>
  <text x="150" y="184" text-anchor="middle" font-size="13.5" font-weight="700" fill="#F97316">diamètre = 36 cm</text>

  <circle cx="42" cy="214" r="9" fill="#FFB938"/>
  <text x="42" y="218" text-anchor="middle" font-size="11" font-weight="700" fill="#3b2303">1</text>
  <text x="60" y="219" font-size="14" font-weight="700" fill="#0f172a">18 × 2 = 36 cm</text>

  <circle cx="212" cy="214" r="9" fill="#FFB938"/>
  <text x="212" y="218" text-anchor="middle" font-size="11" font-weight="700" fill="#3b2303">2</text>
  <text x="230" y="219" font-size="14" font-weight="700" fill="#F97316">36 cm = 360 mm</text>
</svg>`.trim();

// ── Figure Ex. 4 : les SIX termes ──
// Reprend la figure de l'exo 2 en y ajoutant le centre : ce format-ci peut
// attendre « centre » comme réponse, l'exo 2 non. Chaque coup de pouce couvre
// exactement les termes de SA règle — les formats sont indépendants et un
// exercice peut ne jamais être donné.
const SVG_LEXIQUE_COMPLET = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 360 232" role="img"
     aria-label="À gauche un cercle avec son centre, un rayon, une corde et un diamètre ; à droite un disque rempli">
  <circle cx="95" cy="80" r="58" stroke="#1E3A8A" fill="none" stroke-width="2.5"/>
  <line x1="41" y1="60" x2="149" y2="100" stroke="#F97316" stroke-width="2.5"/>
  <line x1="51" y1="117" x2="105" y2="137" stroke="#1D9E75" stroke-width="2.5"/>
  <line x1="95" y1="80" x2="128" y2="33" stroke="#378ADD" stroke-width="2.5"/>
  <g fill="#0f172a">
    <circle cx="128" cy="33" r="2.5"/><circle cx="41" cy="60" r="2.5"/>
    <circle cx="149" cy="100" r="2.5"/><circle cx="51" cy="117" r="2.5"/>
    <circle cx="105" cy="137" r="2.5"/>
  </g>
  <circle cx="95" cy="80" r="4.5" fill="#E24B4A"/>
  <text x="95" y="160" text-anchor="middle" font-size="15" font-weight="700" fill="#1E3A8A">cercle</text>
  <text x="95" y="177" text-anchor="middle" font-size="11" fill="#64748b">le contour seul</text>

  <circle cx="268" cy="80" r="58" fill="#93C5FD" stroke="#1E3A8A" stroke-width="2.5"/>
  <circle cx="268" cy="80" r="4.5" fill="#E24B4A"/>
  <text x="268" y="160" text-anchor="middle" font-size="15" font-weight="700" fill="#1E3A8A">disque</text>
  <text x="268" y="177" text-anchor="middle" font-size="11" fill="#64748b">le contour et l'intérieur</text>

  <circle cx="28" cy="204" r="4.5" fill="#E24B4A"/>
  <g stroke-width="3">
    <line x1="96" y1="204" x2="116" y2="204" stroke="#378ADD"/>
    <line x1="176" y1="204" x2="196" y2="204" stroke="#1D9E75"/>
    <line x1="256" y1="204" x2="276" y2="204" stroke="#F97316"/>
  </g>
  <g font-size="12.5" fill="#334155">
    <text x="38" y="208">centre</text>
    <text x="122" y="208">rayon</text>
    <text x="202" y="208">corde</text>
    <text x="282" y="208">diamètre</text>
  </g>
</svg>`.trim();

// ── Figure Ex. 5 : rayon [AB] contre diamètre [AB] ──
// Les deux cas sur le MÊME segment [AB], côte à côte : c'est leur contraste qui
// fait la notion. À gauche le segment sert de rayon et le centre est ailleurs ;
// à droite il sert de diamètre, donc le centre tombe sur son milieu et le cercle
// passe par A et par B. L'erreur classique — centrer sur A ou sur B — n'est
// évitable que si l'élève a vu les deux dispositions ensemble.
const SVG_RAYON_OU_DIAMETRE = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 360 236" role="img"
     aria-label="À gauche un cercle de rayon AB centré ailleurs, à droite un cercle de diamètre AB centré au milieu de AB">
  <line x1="20" y1="34" x2="80" y2="34" stroke="#F97316" stroke-width="3"/>
  <line x1="20" y1="28" x2="20" y2="40" stroke="#F97316" stroke-width="2"/>
  <line x1="80" y1="28" x2="80" y2="40" stroke="#F97316" stroke-width="2"/>
  <text x="20" y="22" text-anchor="middle" font-size="12" fill="#0f172a">A</text>
  <text x="80" y="22" text-anchor="middle" font-size="12" fill="#0f172a">B</text>

  <circle cx="95" cy="118" r="60" stroke="#1E3A8A" fill="none" stroke-width="2.5"/>
  <line x1="95" y1="118" x2="155" y2="118" stroke="#F97316" stroke-width="3"/>
  <circle cx="95" cy="118" r="4.5" fill="#E24B4A"/>
  <text x="86" y="114" text-anchor="end" font-size="12" font-weight="700" fill="#E24B4A">centre</text>
  <text x="95" y="196" text-anchor="middle" font-size="13" font-weight="700" fill="#1E3A8A">rayon [AB]</text>
  <text x="95" y="214" text-anchor="middle" font-size="11" fill="#64748b">le centre est donné,</text>
  <text x="95" y="228" text-anchor="middle" font-size="11" fill="#64748b">AB est le rayon</text>

  <circle cx="268" cy="118" r="30" stroke="#1E3A8A" fill="none" stroke-width="2.5"/>
  <line x1="238" y1="118" x2="298" y2="118" stroke="#F97316" stroke-width="3"/>
  <line x1="238" y1="112" x2="238" y2="124" stroke="#F97316" stroke-width="2"/>
  <line x1="298" y1="112" x2="298" y2="124" stroke="#F97316" stroke-width="2"/>
  <circle cx="268" cy="118" r="4.5" fill="#E24B4A"/>
  <text x="238" y="106" text-anchor="middle" font-size="12" fill="#0f172a">A</text>
  <text x="298" y="106" text-anchor="middle" font-size="12" fill="#0f172a">B</text>
  <text x="268" y="142" text-anchor="middle" font-size="11" font-weight="700" fill="#E24B4A">milieu</text>
  <text x="268" y="196" text-anchor="middle" font-size="13" font-weight="700" fill="#1E3A8A">diamètre [AB]</text>
  <text x="268" y="214" text-anchor="middle" font-size="11" fill="#64748b">le centre est le milieu,</text>
  <text x="268" y="228" text-anchor="middle" font-size="11" fill="#64748b">le cercle passe par A et B</text>
</svg>`.trim();

const CP_655 = {
    vocabulaire_cercle_vf_figure: {
        // Page unique (revue 2026-08-11) : la règle verbale tournait autour
        // des verdicts sans les dire — plus déroutant qu'aidant. La figure-
        // lexique suffit : elle NOMME les objets (autres lettres, autre
        // cercle), le tri Vrai/Faux reste entièrement à la charge de l'élève.
        pages: [
            { titre: 'Un exemple', svg: SVG_LEXIQUE_CERCLE },
        ],
    },

    lexique_cercle_qcm: {
        pages: [
            {
                titre: 'La règle',
                // Les CINQ termes : ce QCM les tire tous, alors que l'exo 1 n'en
                // teste que trois. Cercle et disque d'abord — c'est la confusion
                // que la figure de la page 2 vient trancher.
                lignes: [
                    '**Cercle** = tous les points situés à la même distance du centre. C\'est le **contour** seul.',
                    '**Disque** = le cercle **et** tout ce qu\'il y a à l\'intérieur.',
                    '**Rayon** = segment qui relie le centre à un point du cercle.',
                    '**Corde** = segment qui relie deux points du cercle.',
                    '**Diamètre** = corde qui passe par le centre.',
                ],
            },
            { titre: 'Un exemple', svg: SVG_CERCLE_DISQUE },
        ],
    },

    rayon_diametre_saisie: {
        pages: [
            {
                titre: 'La règle',
                lignes: [
                    'Le **diamètre** vaut **deux fois** le rayon.',
                    'Donc le **rayon** vaut le diamètre **divisé par 2**.',
                    'Si la réponse est demandée en **mm**, convertis à la fin (1 cm = 10 mm).',
                ],
                svg: SVG_CONVERSION,
            },
            { titre: 'Un exemple', svg: SVG_RAYON_DIAMETRE },
        ],
    },

    trou_vocabulaire_qcm: {
        pages: [
            {
                titre: 'La règle',
                // Les SIX termes : depuis l'ajout de la 6e structure, « centre »
                // peut être la bonne réponse. Le centre en tête, c'est le point
                // auquel les cinq autres définitions se réfèrent.
                lignes: [
                    '**Centre** = le point situé à égale distance de tous les points du cercle.',
                    '**Cercle** = tous les points situés à la même distance du centre. C\'est le **contour** seul.',
                    '**Disque** = le cercle **et** tout ce qu\'il y a à l\'intérieur.',
                    '**Rayon** = segment qui relie le centre à un point du cercle.',
                    '**Corde** = segment qui relie deux points du cercle.',
                    '**Diamètre** = corde qui passe par le centre.',
                ],
            },
            { titre: 'Un exemple', svg: SVG_LEXIQUE_COMPLET },
        ],
    },

    construction_cercles_programme: {
        pages: [
            {
                titre: 'La règle',
                // Les deux tâches symboliques du format. Contrairement à la
                // construction de gen_654, entièrement guidée par des nombres,
                // celle-ci demande d'interpréter « rayon [AB] » et surtout
                // « diamètre [AB] » — où l'erreur classique est de centrer sur A.
                lignes: [
                    'Cercle de **rayon [AB]** : écarte le compas de A à B, puis pique sur le centre demandé.',
                    'Cercle de **diamètre [AB]** : le centre est le **milieu** de [AB], et le rayon vaut la **moitié** de AB.',
                ],
            },
            { titre: 'Un exemple', svg: SVG_RAYON_OU_DIAMETRE },
        ],
    },
};

export default CP_655;
